import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCalendarDay, faBoxArchive, faPen, faCheck, faEye } from '@fortawesome/free-solid-svg-icons';
import { formatDate } from '../../../../utils/dateUtils';
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
  DragEndEvent,
} from '@dnd-kit/core';
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  useSortable,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { Badge, Dropdown, KebabMenu, PulseDot } from '../../../ui';
import { taskStatusMeta, type ClientTaskRow } from './taskMeta';

interface User {
  id: string;
  name: string;
  email: string;
}

interface BacklogTasksListProps {
  tasks: ClientTaskRow[];
  users: User[];
  onAssign: (taskId: string, userId: string, userName: string) => void;
  onStatusChange: (taskId: string, newStatus: string) => Promise<void>;
  onArchive: (taskId: string) => void;
  onView: (task: ClientTaskRow) => void;
  isAdmin: boolean;
  onReorder: (tasks: ClientTaskRow[]) => void;
}

interface SortableTaskItemProps {
  task: ClientTaskRow;
  users: User[];
  isAdmin: boolean;
  onAssign: (taskId: string, userId: string, userName: string) => void;
  onStatusChange: (taskId: string, newStatus: string) => Promise<void>;
  onArchive: (taskId: string) => void;
  onView: (task: ClientTaskRow) => void;
}

function SortableTaskItem({
  task,
  users,
  isAdmin,
  onAssign,
  onStatusChange,
  onArchive,
  onView,
}: SortableTaskItemProps) {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({ id: task.id });
  const meta = taskStatusMeta(task.status);

  const style: React.CSSProperties = {
    transform: CSS.Transform.toString(transform),
    transition,
    zIndex: isDragging ? 100 : 1,
    opacity: isDragging ? 0.85 : 1,
    boxShadow: isDragging ? 'var(--shadow-hover)' : 'none',
  };

  return (
    <div
      ref={setNodeRef}
      className="hover-wash"
      style={{
        display: 'flex',
        alignItems: 'center',
        gap: '10px',
        padding: '9px 10px',
        margin: '0 -10px',
        borderRadius: 'var(--radius-sm)',
        cursor: 'pointer',
        background: isDragging ? 'var(--surface-raised)' : undefined,
        ...style,
      }}
      {...attributes}
      {...listeners}
      onClick={() => onView(task)}
    >
      <PulseDot tone={meta.tone} pulse={false} title={task.status} />
      <span style={{ flex: 1, minWidth: 0 }}>
        <span
          className="line-clamp-2 md:line-clamp-none md:block md:truncate"
          style={{
            fontSize: 'var(--text-sm)',
            color: 'var(--text-body)',
            overflowWrap: 'anywhere',
          }}
        >
          {task.title}
        </span>
        {(task.due_date || task.website_title) && (
          <span
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: '12px',
              marginTop: '2px',
              fontSize: 'var(--text-xs)',
              color: 'var(--text-faint)',
            }}
          >
            {task.due_date ? (
              <span className="t-num" style={{ display: 'inline-flex', alignItems: 'center', gap: '5px' }}>
                <FontAwesomeIcon icon={faCalendarDay} style={{ fontSize: '10px' }} />
                {formatDate(task.due_date, 'MMM d')}
              </span>
            ) : null}
            {task.website_title ? <span>{task.website_title}</span> : null}
          </span>
        )}
      </span>
      <span onClick={(e) => e.stopPropagation()} style={{ width: '148px', flexShrink: 0 }}>
        <Dropdown
          size="sm"
          placeholder="Assign..."
          options={users.map((u) => ({ value: u.id, label: u.name }))}
          value={task.assigned_to || ''}
          onChange={(e) => {
            const userId = e.target.value;
            const userName = users.find((u) => u.id === userId)?.name || '';
            if (userId) onAssign(task.id, userId, userName);
          }}
        />
      </span>
      <Badge tone={meta.tone}>{task.status}</Badge>
      <KebabMenu
        label="Task actions"
        actions={[
          task.status !== 'Complete'
            ? { label: 'Mark Complete', icon: faCheck, onClick: () => onStatusChange(task.id, 'Complete') }
            : null,
          task.status !== 'Submitted'
            ? { label: 'Mark Submitted', icon: faEye, onClick: () => onStatusChange(task.id, 'Submitted') }
            : null,
          { label: 'Edit Task', icon: faPen, onClick: () => onView(task) },
          isAdmin
            ? { label: 'Archive', icon: faBoxArchive, danger: true, onClick: () => onArchive(task.id) }
            : null,
        ]}
      />
    </div>
  );
}

export default function BacklogTasksList({
  tasks,
  users,
  onAssign,
  onStatusChange,
  onArchive,
  onView,
  isAdmin,
  onReorder,
}: BacklogTasksListProps) {
  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 8 } }),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates })
  );

  const handleDragEnd = (event: DragEndEvent) => {
    const { active, over } = event;
    if (!over || active.id === over.id) return;
    const oldIndex = tasks.findIndex((t) => t.id === active.id);
    const newIndex = tasks.findIndex((t) => t.id === over.id);
    if (oldIndex === -1 || newIndex === -1) return;
    const reordered = arrayMove([...tasks], oldIndex, newIndex);
    onReorder(reordered);
  };

  return (
    <div style={{ display: 'flex', flexDirection: 'column' }}>
      <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleDragEnd}>
        <SortableContext items={tasks.map((t) => t.id)} strategy={verticalListSortingStrategy}>
          {tasks.map((task) => (
            <SortableTaskItem
              key={task.id}
              task={task}
              users={users}
              isAdmin={isAdmin}
              onAssign={onAssign}
              onStatusChange={onStatusChange}
              onArchive={onArchive}
              onView={onView}
            />
          ))}
        </SortableContext>
      </DndContext>
      {tasks.length === 0 && (
        <p style={{ margin: 0, fontSize: 'var(--text-sm)', color: 'var(--text-faint)' }}>
          Nothing parked. Unassigned tasks wait here until they get an owner.
        </p>
      )}
    </div>
  );
}
